import { Node } from "#/decision_tree_viewer/models/node";
import { useSnippetProcessor } from "#/decision_tree_viewer/composables/useSnippetProcessor";
import { stripHtml } from "#/shared/utils/string";

const { processText } = useSnippetProcessor();

export class QuestionNode extends Node {
  get defaults() {
    return {
      ...super.defaults,
      name: "QuestionNode",

      title: undefined,
      message: undefined,

      inputType: undefined,

      answers: [],
      selectedAnswerId: undefined,

      // Free-text the visitor types when inputType is "text-entry"
      enteredText: undefined,
      // Hardcoded path followed when the entered text matches no answer
      noMatchTargetNodeId: undefined,

      sendAsSchedulingNote: false,
    };
  }

  get currentState() {
    return { selectedAnswerId: this.selectedAnswerId, enteredText: this.enteredText };
  }

  get context() {
    const context = {
      [`question-${this.id}`]: this.selectedAnswer,
    };

    if (this.isTextEntryInputType) {
      context[`question-${this.id}-text`] = this.enteredText;
    }

    return context;
  }

  get possibleNextNodeIds() {
    const ids = this.answers.map((answer) => answer.targetNodeId);

    if (this.isTextEntryInputType && this.noMatchTargetNodeId) {
      ids.push(this.noMatchTargetNodeId);
    }

    return ids;
  }

  get questionText() {
    return this.title || this.message || "";
  }

  get analyticsSummary() {
    if (!this.selectedAnswer) {
      return null;
    }
    return {
      nodeType: this.name,
      nodeId: this.id,
      questionText: stripHtml(this.questionText).trim(),
      selectedAnswer: this.selectedAnswer.title,
      selectedAnswerId: this.selectedAnswerId,
    };
  }

  get selectedAnswer() {
    if (this.isTextEntryInputType) {
      return this.matchedAnswer;
    }

    return this.answers.find((answer) => answer.id === this.selectedAnswerId);
  }

  get isRadioInputType() {
    return this.inputType === "radio";
  }

  get isTextEntryInputType() {
    return this.inputType === "text-entry";
  }

  // Whether the visitor has committed text for a text-entry question
  get hasEnteredText() {
    return Boolean(this.enteredText?.trim());
  }

  // The answer whose match terms match the entered text, or undefined when none match
  get matchedAnswer() {
    if (!this.hasEnteredText) {
      return undefined;
    }

    const text = this.normalizeMatchTerm(this.enteredText);

    return this.answers.find((answer) => this.answerMatchesText(answer, text));
  }

  answerMatchesText(answer, normalizedText) {
    return this.matchTermsFor(answer).some((term) => term === normalizedText);
  }

  // The terms an answer matches against: its authored keywords, falling back to its title
  matchTermsFor(answer) {
    const source = answer.keywords?.trim() ? answer.keywords : answer.title;

    return (source ?? "")
      .split(",")
      .map((term) => this.normalizeMatchTerm(term))
      .filter(Boolean);
  }

  normalizeMatchTerm(value) {
    return (value ?? "").trim().toLowerCase();
  }

  beforeShow(context) {
    if (this.isTextEntryInputType) {
      this.enteredText = context[`question-${this.id}-text`];
    } else {
      this.selectedAnswerId = context[`question-${this.id}`]?.id;
    }
  }

  autoAdvance(context) {
    // If we've already shown this question, we don't need to show it again
    if (this.isTextEntryInputType) {
      return Boolean(context[`question-${this.id}-text`]?.trim());
    }

    return context[`question-${this.id}`];
  }

  nextNodeId() {
    if (this.isTextEntryInputType) {
      // Don't advance until the visitor has entered text
      if (!this.hasEnteredText) {
        return undefined;
      }

      return this.selectedAnswer?.targetNodeId ?? this.noMatchTargetNodeId;
    }

    return this.selectedAnswer?.targetNodeId;
  }

  toSchedulingNote(context) {
    if (!this.sendAsSchedulingNote || !this.selectedAnswer) {
      return undefined;
    }

    let noteTitle = stripHtml(processText(this.title, context)).trim();

    if (!noteTitle) {
      noteTitle = stripHtml(processText(this.message, context)).trim();
    }

    return `${noteTitle} ${this.selectedAnswer.title}`;
  }

  reset() {
    this.selectedAnswerId = undefined;
    this.enteredText = undefined;
  }
}
